<template>
    <main>
        <div class="container">
            <div class="row">
                <div class="col-12">
                    <div class="page-heading">
                        <h2>Vote for <span>Exiles</span></h2>
                        <p>You are currently voting for your account: <strong>{{ voteUsername || 'Loading...' }}</strong> - If you would like to vote for another account, please sign out in the top right corner of the website and sign back in with the account you wish to vote for.</p>
                    </div>
                </div>
            </div>

            <div class="main-content-box">
                <div class="vote-lookup">
                    <p v-if="loadingStatuses" class="vote-lookup__note">Loading your vote status...</p>
                    <p v-else class="vote-lookup__note">If you are currently logged into your account, you will receive your rewards immediately. Otherwise, you will receive your rewards when you sign into your account again.</p>
                </div>

                <div class="vote-buttons--grid">
                    <a
                        :href="rulocusUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="vote_btn"
                        :class="isVoteAvailable('RULOCUS') ? 'available' : 'voted'"
                    >
                        <div class="site-name">RuLocus</div>
                        <div class="next-vote">Next Vote: <span>{{ formattedCountdown('RULOCUS') }}</span></div>
                    </a>
                    <a
                        :href="rspsListUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="vote_btn"
                        :class="isVoteAvailable('RSPS_LIST') ? 'available' : 'voted'"
                    >
                        <div class="site-name">Rsps-List</div>
                        <div class="next-vote">Next Vote: <span>{{ formattedCountdown('RSPS_LIST') }}</span></div>
                    </a>
                </div>
            </div>
        </div>
    </main>
</template>

<script>
import APIClient from "@/services/APIClient.js"

export default {
    created() {
        document.title = 'Vote - Exiles'
        this.apiClient = new APIClient()
        this.$store.dispatch('getAuthenticatedUser')
    },
    mounted() {
        this.$watch(
            () => this.voteUserId,
            (value) => {
                if (value) {
                    this.loadVoteStatuses()
                }
            },
            { immediate: true }
        )
    },
    beforeUnmount() {
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval)
        }
        if (this.statusRefreshInterval) {
            clearInterval(this.statusRefreshInterval)
        }
    },
    data() {
        return {
            apiClient: null,
            statuses: [],
            statusesLoadedAt: null,
            countdownInterval: null,
            statusRefreshInterval: null,
            now: Date.now(),
            loadingStatuses: false
        }
    },
    computed: {
        voteUsername() {
            return this.$store.state.user && this.$store.state.user.name ? this.$store.state.user.name : ''
        },
        voteUserId() {
            if (this.$store.state.user && this.$store.state.user.id) {
                return this.$store.state.user.id
            }

            if (this.$store.state.user && this.$store.state.user.userId) {
                return this.$store.state.user.userId
            }

            if (this.$store.state.user && this.$store.state.user.playerId) {
                return this.$store.state.user.playerId
            }

            if (this.$store.state.user && this.$store.state.user.accountId) {
                return this.$store.state.user.accountId
            }

            return null
        },
        rulocusUrl() {
            return `https://www.rulocus.com/top-rsps-list/exiles-closed-beta/vote/?callback=${this.voteUserId}`
        },
        rspsListUrl() {
            return `https://www.rsps-list.com/index.php?a=in&u=exilesps&id=${this.voteUserId}`
        }
    },
    methods: {
        loadVoteStatuses() {
            this.loadingStatuses = true
            this.apiClient.getVoteStatuses().then(response => {
                this.statuses = Array.isArray(response.data) ? response.data : []
                this.statusesLoadedAt = Date.now()
                this.now = Date.now()

                if (this.countdownInterval) {
                    clearInterval(this.countdownInterval)
                }
                if (this.statusRefreshInterval) {
                    clearInterval(this.statusRefreshInterval)
                }

                this.countdownInterval = setInterval(() => {
                    this.now = Date.now()
                }, 1000)
                this.statusRefreshInterval = setInterval(() => {
                    this.refreshVoteStatuses()
                }, 15000)
            }).catch((error) => {
                console.log(error)
                this.statuses = []
            }).finally(() => {
                this.loadingStatuses = false
            })
        },
        refreshVoteStatuses() {
            if (!this.voteUserId) {
                return
            }

            this.apiClient.getVoteStatuses().then(response => {
                this.statuses = Array.isArray(response.data) ? response.data : []
                this.statusesLoadedAt = Date.now()
                this.now = Date.now()
            }).catch((error) => {
                console.log(error)
            })
        },
        statusFor(site) {
            return this.statuses.find((status) => status.type === site) || null
        },
        secondsRemaining(site) {
            const status = this.statusFor(site)
            if (!status || this.statusesLoadedAt === null) {
                return null
            }

            const elapsedSeconds = Math.floor((this.now - this.statusesLoadedAt) / 1000)
            return Math.max(0, status.secondsTillCanVote - elapsedSeconds)
        },
        formattedCountdown(site) {
            if (this.loadingStatuses) {
                return 'Loading...'
            }

            const remaining = this.secondsRemaining(site)
            if (remaining === null) {
                return 'Unavailable'
            }

            if (remaining === 0) {
                return 'Available!'
            }

            const hours = Math.floor(remaining / 3600)
            const minutes = Math.floor((remaining % 3600) / 60)
            const seconds = remaining % 60
            return `${hours > 0 ? `${hours} hours, ` : ''}${minutes > 0 ? `${minutes} minutes, ` : ''}${seconds} seconds`
        },
        isVoteAvailable(site) {
            return this.secondsRemaining(site) === 0
        }
    }
}
</script>

<style scoped>
.vote-lookup {
    max-width: 700px;
    margin: 0 auto 1.5rem;
}

.vote-lookup__note {
    margin-top: 0.75rem;
    color: #a59d85;
    text-align: center;
}

.vote-buttons--grid {
    display: flex;
    justify-content: center;
    gap: 1rem;
    flex-wrap: wrap;
}

.vote-buttons--grid .vote_btn .site-name {
    font-size: 1.15rem;
    font-weight: 700;
    height: auto;
    padding: 12px 10px 6px;
}

.vote-buttons--grid .vote_btn .next-vote {
    font-size: 0.95rem;
    height: auto;
    padding: 6px 10px 12px;
}
</style>
